import { parseJsonFilter } from '@/hooks/use-table-filter/utils';
import { getGlobalFilterQuery } from '@/lib/global-filter/get-global-filter-query';
import { getConfig } from '@/lib/get-config';
import { type BaseInstance, type ModelName } from '@pigello/pigello-matrix';
import { fetchApi } from '.';
import type { ListArguments } from '../types';
import {
  buildListBackendUrl,
  handleApiResponse,
  sanitizeFilters,
} from './utils';
import { rewriteFilter } from '../hooks/filter-rewrites';

type GetCount = {
  modelName: ModelName;
  filters?: ListArguments['queryParams']['filters'];
  revalidate?: number;
  overrideUrl?: string;
  overrideUrlReplaceAll?: boolean;
  enableGlobalFilters?: boolean;
  signal?: AbortSignal;
};

export async function getCount({
  modelName,
  filters,
  revalidate,
  overrideUrl,
  overrideUrlReplaceAll,
  enableGlobalFilters,
  signal,
}: GetCount) {
  const config = getConfig<ModelName, BaseInstance>(modelName, true);
  if (!config) {
    throw new Error(`No config found for model ${modelName}`);
  }

  // Rewrite filters
  const rewrittenFilters = await rewriteFilter(filters);

  // Sanitize filters
  const sanitizedFilters = sanitizeFilters(rewrittenFilters);

  // Turn the sanitized filters into a string
  const sanitizedFiltersString = parseJsonFilter(sanitizedFilters ?? {});

  // Build backend URL
  const backendUrl = buildListBackendUrl(
    config,
    overrideUrl,
    overrideUrlReplaceAll
  );

  // Construct complete URL with global filters if applicable
  const globalFilterQuery =
    modelName !== 'organization'
      ? await getGlobalFilterQuery(enableGlobalFilters)
      : '';

  const completeUrl = `${backendUrl}${globalFilterQuery ? `${globalFilterQuery.replace('&', '?')}` : '?'
    }`;

  // Append JSON filters
  const urlWithFilters = `${completeUrl}&${sanitizedFiltersString.length > 0 ? sanitizedFiltersString : ''}`;

  const finalFinalUrl =
    urlWithFilters.endsWith('&') || urlWithFilters.endsWith('?')
      ? urlWithFilters.slice(0, -1)
      : urlWithFilters;

  const finalUrl = `${finalFinalUrl}&_statistics=true`.replace('&&', '&'); // remove double ampersands

  const res = await fetchApi({
    method: 'GET',
    url: finalUrl,
    next: {
      revalidate,
    },
    signal,
  });

  const data = await handleApiResponse<{
    statistics: { count: number };
  }>(res);
  return data.statistics.count;
}
